import React from 'react';
import 'twin.macro';
import tw from 'twin.macro';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import Select from '@teetsh/app/src/components/Form/Select2';
import Input from 'components/Form/Input2';
import DateInput from '@teetsh/app/src/components/Form/Dateinput2';
import { EleveMappingResult } from './types/ClassroomActions';
import { useTranslation } from 'react-i18next';
import { UserLocale } from '@teetsh/app/src/types/user';
import { format } from 'utils/time/format';

interface Props {
  students: EleveMappingResult[];
  classroomId: string;
  onUpdateEleve: ({
    classroomId,
    eleve,
  }: {
    classroomId: string;
    eleve: any;
  }) => void;
  enableSkipCheckbox?: boolean;
  enableEdit?: boolean;
}

export function StudentTable({
  students,
  classroomId,
  onUpdateEleve,
  enableSkipCheckbox = true,
  enableEdit = true,
}: Props) {
  const { t } = useTranslation(['common', 'eleve']);
  return (
    <div tw="flex flex-col gap-2">
      <div
        tw="grid gap-2.5 text-sm font-normal text-neutral-moderate"
        style={{
          gridTemplateColumns: enableSkipCheckbox
            ? 'auto 1fr 1fr 1fr 1fr 1fr'
            : '1fr 1fr 1fr 1fr 1fr',
        }}
      >
        {enableSkipCheckbox && (
          <>
            {/* Empty space matching checkbox width */}
            <div tw="w-4 h-4"></div>
          </>
        )}
        <div>{t('eleve:fields.givenName')}</div>
        <div>{t('eleve:fields.familyName')}</div>
        <div>{t('eleve:fields.birthday')}</div>
        <div>{t('eleve:fields.gender')}</div>
        <div>{t('eleve:fields.ineShort')}</div>
      </div>

      {students.map(student => (
        <div
          key={student.id}
          tw="grid gap-2"
          style={{
            gridTemplateColumns: enableSkipCheckbox
              ? 'auto 1fr 1fr 1fr 1fr 1fr'
              : '1fr 1fr 1fr 1fr 1fr',
          }}
        >
          {enableSkipCheckbox && (
            <div tw="flex items-center justify-start h-9">
              <Checkbox
                checked={student.action !== 'skip'}
                aria-label={`Élève ${student.givenName} ${student.familyName}`}
                onChange={e => {
                  onUpdateEleve({
                    classroomId,
                    eleve: {
                      ...student,
                      action: e.target.checked ? 'create' : 'skip',
                    },
                  });
                }}
              />
            </div>
          )}

          <div tw="h-9 flex items-center">
            <Input
              value={student.givenName}
              disabled={student.action === 'skip' || !enableEdit}
              onChange={e => {
                onUpdateEleve({
                  classroomId,
                  eleve: { ...student, givenName: e?.currentTarget?.value },
                });
              }}
            />
          </div>

          <div tw="h-9 flex items-center">
            <Input
              value={student.familyName}
              disabled={student.action === 'skip' || !enableEdit}
              onChange={e => {
                onUpdateEleve({
                  classroomId,
                  eleve: { ...student, familyName: e?.currentTarget?.value },
                });
              }}
            />
          </div>

          <div tw="h-9 flex items-center">
            {enableEdit ? (
              <DateInput
                id={`student-${student.id}-birthday`}
                userLocale={t('common:locale.long') as UserLocale}
                canBeNull
                value={student.birthday}
                onChange={v => {
                  if (student.action === 'skip') return;
                  onUpdateEleve({
                    classroomId,
                    eleve: { ...student, birthday: v },
                  });
                }}
                css={[
                  student.action === 'skip' &&
                    tw`pointer-events-none bg-neutral-quiet! text-neutral-supershy!`, // override disabled style to match other disabled inputs
                ]}
              />
            ) : (
              <Input
                id={`student-${student.id}-birthday`}
                value={
                  student.birthday
                    ? format(
                        student.birthday,
                        t('common:locale.long') as UserLocale,
                        t('common:datePattern.dd/MM/yyyy'),
                      )
                    : ''
                }
                disabled
              />
            )}
          </div>

          <div tw="h-9 flex items-center">
            <Select
              value={student.gender || ''}
              fill
              disabled={student.action === 'skip' || !enableEdit}
              tw="bg-white font-normal"
              css={[
                (student.action === 'skip' || !enableEdit) &&
                  tw`opacity-100! bg-neutral-quiet border-neutral-quiet! text-neutral-supershy! cursor-default! `, // override disabled style to match other disabled inputs
              ]}
              onChange={v => {
                onUpdateEleve({
                  classroomId,
                  eleve: { ...student, gender: v },
                });
              }}
              options={[
                {
                  label: t('eleve:fields.boy'),
                  value: 'H',
                },
                {
                  label: t('eleve:fields.girl'),
                  value: 'F',
                },
              ]}
            />
          </div>

          <div tw="h-9 flex items-center">
            <Input
              value={student.ine || ''}
              disabled={student.action === 'skip' || !enableEdit}
              placeholder={t('eleve:fields.inePlaceholder')}
              onChange={e => {
                onUpdateEleve({
                  classroomId,
                  eleve: { ...student, ine: e?.currentTarget?.value },
                });
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
